import React, { Fragment, useContext, useEffect, useState } from 'react';

import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import { Combobox, Transition } from '@headlessui/react';
import { Check, ChevronsUpDown, MapPin } from 'lucide-react';

import { ToastContext } from '../../../../components/Toast';

import clsx from 'clsx';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

const fetchRestaurants = async ({ queryKey, signal }) => {
  const [, searchTerm] = queryKey;
  
  if (!searchTerm) {
    return [];
  }
  
  const { data } = await axios.get(`${import.meta.env.VITE_API_URL}api/v4/locachecker/places`, {
    params: { textQuery: searchTerm },
    signal,
  });
  
  return data;
};

const RestaurantSearch = ({ selectedRestaurant, setSelectedRestaurant }) => {
  const [query, setQuery] = useState('');
  
  const debouncedQuery = useDebounce(query, 1000);
  
  const { addToast } = useContext(ToastContext);
  
  const { data: options = [], isLoading, isError, error } = useQuery({
    queryKey: ['restaurants', debouncedQuery],
    queryFn: fetchRestaurants,
    enabled: !!debouncedQuery,
    retry: (failureCount, error) => {
      if (error.response?.status === 429) {
        return false;
      }
      return failureCount < 3;
    },
  });
  
  useEffect(() => {
    if (isError) {
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        addToast('You have made too many search requests. Please wait a few minutes and try again.');
      } else {
        addToast('An unexpected error occurred.');
      }
    }
  }, [isError, error, addToast]);
  
  
  return (
    <>
      <Combobox value={selectedRestaurant} onChange={setSelectedRestaurant}>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MapPin className="h-5 w-5 text-gray-400"/>
          </div>
          
          <Combobox.Input
            className="input-field pl-10"
            onChange={(event) => setQuery(event.target.value)}
            displayValue={(restaurant) => restaurant?.displayName}
            placeholder="Enter your restaurant name..."
            autoComplete="off"
          />
          
          {!!options.length && <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronsUpDown className="h-5 w-5 text-gray-400" aria-hidden="true"/>
          </Combobox.Button>}
        </div>
        
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Combobox.Options
            className="absolute mt-1 max-h-60 w-full max-w-lg overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-10">
            {isLoading && <div className="relative cursor-default select-none py-2 px-4 text-gray-700">Searching for the restaurant...</div>}
            
            {!isLoading && options.length === 0 && query !== '' && (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                Nothing found.
              </div>
            )}
            
            {options.map((option, i) => (
              <Combobox.Option
                key={i}
                value={option}
                className={({ active }) => clsx('relative cursor-default select-none py-2 pl-10 pr-4', active ? 'bg-primary-600 text-white' : 'text-gray-900')}
              >
                {({ selected, active }) => (
                  <>
                    <span className={clsx('block truncate font-medium', active ? 'text-white' : '')}>
                      {option.displayName}
                    </span>
                    
                    <span
                      className={clsx('block truncate text-sm text-gray-600 font-normal', active ? 'text-white' : '')}>
                      {option.formattedAddress}
                    </span>
                    
                    {selected && (
                      <span
                        className={clsx('absolute inset-y-0 left-0 flex items-center pl-3', active ? 'text-white' : 'text-primary-600')}>
                        <Check className="h-5 w-5" aria-hidden="true"/>
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Transition>
      </Combobox>
    </>
  );
};

export default RestaurantSearch;
